<template>
  <div class="accountStatements">
    <div class="searchTop">
      <van-row type="flex" align="center" class="search-top-row">
        <van-col class="colItem1">
          <van-search
            v-model="searchText"
            show-action
            shape="round"
            background="transparent"
            :placeholder="getPlaceHolder()"
          >
            <template #action>
              <div @click="onSearch">{{ $t.meta.search }}</div>
            </template>
          </van-search>
        </van-col>
        <van-col class="colItem2">
          <div :class="isFilter() ? 'isFilter' : ''">
            <span @click="showFilter = true">{{ $t.meta.filter }}</span>
          </div>
        </van-col>
      </van-row>
    </div>

    <van-list
      class="list"
      v-model="loading"
      :finished="finished"
      :finished-text="$t.meta.noMore"
      :error.sync="error"
      :error-text="errorText"
      @load="getList(page + 1)"
    >
      <div
        class="list-container"
        v-for="(item, index) in tableData"
        :key="index"
      >
        <div class="checkBox">
          <checkbox
            v-model="item.isSelected"
            @input="handleCheckBox"
          ></checkbox>
        </div>
        <div class="container" @click="gotoDetail(item.CTId)">
          <div class="list-item">
            {{ $t.clientFund.serialRegistrationNumber }}：{{
              item.SerialRegistrationNumber
            }}
          </div>
          <div class="list-item">
            {{ $t.clientFund.fundClass }}：
            <span v-if="item.PaymentType == 1">{{
              $t.clientFund.paymentType1
            }}</span>
            <span v-if="item.PaymentType == 2">{{
              $t.clientFund.paymentType2
            }}</span>
          </div>
          <div class="list-item">
            {{ $t.clientFund.accountName }}：{{ item.AccountName }}
          </div>
          <div class="list-item">
            {{ $t.clientFund.remitterName }}：{{ item.RemitterName }}
          </div>
          <div class="list-item">
            {{ $t.clientFund.customerNumber }}：{{ item.CustomerNumber }}
          </div>
          <div class="list-item">
            {{ $t.clientFund.customerName }}：{{ item.CustomerName }}
          </div>
          <div class="list-item">
            {{ $t.clientFund.receivedAmount }}：{{ item.ReceivedAmount }}
          </div>
          <div class="list-item">
            {{ $t.clientFund.receivedCurrency }}：{{ item.ReceivedCurrencyStr }}
          </div>
          <div class="list-item">
            {{ $t.clientFund.agent }}：{{ item.UserName }}
          </div>
          <div class="list-item">
            {{ $t.clientFund.transferDate }}：{{ item.CreateTime }}
          </div>
        </div>
      </div>
    </van-list>

    <van-popup
      v-model="showFilter"
      position="bottom"
      :style="{ height: '80%', width: '100%' }"
    >
      <div class="popup-header">{{ $t.meta.allFilter }}</div>
      <van-form>
        <div class="radio-label">{{ $t.meta.searchType }}</div>
        <van-radio-group v-model="searchType" direction="horizontal">
          <van-radio
            :name="item.value"
            v-for="(item, index) in searchTypeOptions"
            :key="index"
            >{{ item.text }}</van-radio
          >
        </van-radio-group>

        <div class="radio-label">{{ $t.clientFund.fundClass }}</div>
        <van-radio-group v-model="paymentType" direction="horizontal">
          <van-radio :name="1">{{ $t.clientFund.paymentType1 }}</van-radio>
          <van-radio :name="2">{{ $t.clientFund.paymentType2 }}</van-radio>
        </van-radio-group>

        <div class="radio-label">{{ $t.meta.currency }}</div>
        <van-radio-group v-model="currency" direction="horizontal">
          <van-radio
            :name="currency.SCId"
            v-for="(currency, index) in currencyList"
            :key="index"
            >{{ currency.CurrencySn }}</van-radio
          >
        </van-radio-group>

        <van-field
          v-model="selectedAccount.text"
          :label="$t.clientFund.bankAccount"
          @click="pickerShow('showAccountPicker')"
          :placeholder="$t.meta.select"
          clickable
          readonly
        />

        <van-field
          v-model="minAmount"
          :label="$t.clientFund.minAmount"
          :placeholder="$t.meta.input"
          clickable
        />

        <van-field
          v-model="maxAmount"
          :label="$t.clientFund.maxAmount"
          :placeholder="$t.meta.input"
          clickable
        />

        <div class="custom-date">
          <div class="date-label">{{ $t.meta.date }}</div>
          <van-button
            class="date-btn1"
            type="info"
            size="small"
            @click="showStartDate"
            >{{ $t.meta.beginDate }}</van-button
          >
          <van-button
            class="date-btn2"
            type="info"
            size="small"
            @click="showEndDate"
            >{{ $t.meta.endDate }}</van-button
          >
          <div class="date-txt" v-if="beginTime || endTime">
            {{ beginTime }} {{ $t.meta.to }} {{ endTime }}
          </div>
        </div>
      </van-form>
      <div class="popup-footer">
        <van-button
          class="reset"
          type="info"
          block
          square
          @click="filterReset"
          >{{ $t.meta.reset }}</van-button
        >

        <van-button
          class="confirm"
          type="primary"
          block
          square
          @click="filterSubmit"
          >{{ $t.meta.confirm }}</van-button
        >
      </div>
    </van-popup>

    <van-popup v-model="showStartPicker" position="bottom">
      <van-datetime-picker
        type="date"
        :title="$t.meta.beginDate"
        :formatter="formatter"
        :confirm-button-text="$t.meta.confirm"
        :cancel-button-text="$t.meta.cancel"
        @confirm="dateChange($event, 'start')"
        @cancel="showStartPicker = false"
        :min-date="currentDate"
        v-model="selectDate"
      />
    </van-popup>

    <van-popup v-model="showEndPicker" position="bottom">
      <van-datetime-picker
        type="date"
        :title="$t.meta.endDate"
        :formatter="formatter"
        :confirm-button-text="$t.meta.confirm"
        :cancel-button-text="$t.meta.cancel"
        @confirm="dateChange($event, 'end')"
        @cancel="showEndPicker = false"
        :min-date="currentDate"
        v-model="selectDate"
      />
    </van-popup>

    <van-popup v-model="showAccountPicker" round position="bottom">
      <van-picker
        :label="$t.clientFund.bankAccount"
        show-toolbar
        :columns="accountInfoList"
        :confirm-button-text="$t.meta.confirm"
        :cancel-button-text="$t.meta.cancel"
        @confirm="accountConfirm"
        @cancel="showAccountPicker = false"
      />
    </van-popup>

    <div class="tool-bar" v-if="checkedList && checkedList.length > 0">
      <div class="tool-bar-wrapper">
        <div class="tool-bar-total">
          <div class="tool-bar-total-left">{{ $t.meta.selectTotal }}</div>
          <div
            class="tool-bar-total-item"
            v-for="(item, index) in checkedList"
            :key="index"
          >
            <div class="item1">{{ item.name }}</div>
            <div class="item2">{{ item.value.toFixed(2) }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  List,
  Row,
  Col,
  Search,
  Button,
  Form,
  Field,
  ActionSheet,
  Popup,
  Picker,
  DatetimePicker,
  Radio,
  RadioGroup,
} from "vant";

import Vue from "vue";
Vue.use(List)
  .use(Row)
  .use(Col)
  .use(Search)
  .use(Button)
  .use(Form)
  .use(Field)
  .use(ActionSheet)
  .use(Popup)
  .use(Picker)
  .use(DatetimePicker)
  .use(Radio)
  .use(RadioGroup);

import Checkbox from "@components/checkbox/Checkbox.vue";
export default {
  name: "accountStatements",
  components: {
    Checkbox,
  },
  data() {
    return {
      CompanyId: JSON.parse(localStorage.getItem("auth")).userInfo.CompanyId,
      page: 0,
      pageSize: 10,
      tableData: [],
      loading: false,
      finished: false,
      error: false,
      errorText: "",
      accountInfoList: [],
      currencyList: [],
      //filter
      searchText: "",
      searchType: 1,
      searchTypeOptions: [
        {
          text: this.$t.clientFund.serialRegistrationNumber,
          value: 1,
        },
        {
          text: this.$t.clientFund.remitterName,
          value: 2,
        },
        {
          text: this.$t.clientFund.customerNumber,
          value: 3,
        },
        {
          text: this.$t.clientFund.customerName,
          value: 4,
        },
      ],
      currency: null,
      paymentType: null,
      selectedAccount: { text: "", value: null },
      minAmount: "",
      maxAmount: "",
      showFilter: false,
      showAccountPicker: false,
      //日期
      selectDate: new Date(),
      currentDate: "",
      showStartPicker: false,
      beginTime: "",
      showEndPicker: false,
      endTime: "",
      //选中框
      checkedList: [],
    };
  },
  created() {
    this.getCurrencyList();
    this.getCollectionAccountInfoList();
  },
  methods: {
    initCheckList() {
      this.checkedList = [
        {
          id: this.currencyList[0].SCId,
          name: this.currencyList[0].CurrencySn,
          value: 0,
        },
        {
          id: this.currencyList[1].SCId,
          name: this.currencyList[1].CurrencySn,
          value: 0,
        },
        {
          id: this.currencyList[2].SCId,
          name: this.currencyList[2].CurrencySn,
          value: 0,
        },
      ];
    },
    handleCheckBox() {
      var list = [];
      var items = this.tableData.filter((p) => p.isSelected);
      if (items.length > 0) {
        var C1 = {
          id: this.currencyList[0].SCId,
          name: this.currencyList[0].CurrencySn,
          value: 0,
        };
        var C2 = {
          id: this.currencyList[1].SCId,
          name: this.currencyList[1].CurrencySn,
          value: 0,
        };
        var C3 = {
          id: this.currencyList[2].SCId,
          name: this.currencyList[2].CurrencySn,
          value: 0,
        };
        for (var item of items) {
          if (item.ReceivedCurrency == C1.id) {
            C1.value = C1.value + item.ReceivedAmount;
          }
          if (item.ReceivedCurrency == C2.id) {
            C2.value = C2.value + item.ReceivedAmount;
          }
          if (item.ReceivedCurrency == C3.id) {
            C3.value = C3.value + item.ReceivedAmount;
          }
        }
        list.push(C1);
        list.push(C2);
        list.push(C3);
        this.checkedList = list;
      } else {
        this.initCheckList();
      }
    },
    gotoDetail(id) {
      this.$commonEnv.commonAction(this.$commonEnv.actionType.routerPush, {
        isParams: true,
        path: "accountStatementsDetail",
        name: "accountStatementsDetail",
        query: [
          {
            name: "CTId",
            value: id,
          },
        ],
      });
    },
    getPlaceHolder() {
      var search = this.searchTypeOptions.find(
        (c) => c.value == this.searchType
      );
      if (search) {
        return search.text;
      } else {
        return this.$t.meta.searchText;
      }
    },
    onSearch() {
      this.initList();
      this.getList(1);
    },
    isFilter() {
      if (
        this.paymentType ||
        this.currency ||
        this.selectedAccount.value ||
        this.minAmount ||
        this.maxAmount ||
        (this.beginTime && this.endTime)
      ) {
        return true;
      } else {
        return false;
      }
    },
    filterReset() {
      this.searchText = "";
      this.searchType = 1;
      this.paymentType = null;
      this.currency = null;
      this.selectedAccount = { text: "", value: null };
      this.minAmount = "";
      this.maxAmount = "";
      this.beginTime = "";
      this.endTime = "";
      this.initList();
      this.getList(1);
    },
    filterSubmit() {
      this.initList();
      this.getList(1);
    },
    initList() {
      this.loading = true;
      this.finished = false;
      this.error = false;
      this.showFilter = false;
      this.tableData = [];
      this.initCheckList();
    },
    showStartDate() {
      var start = new Date();
      start.setTime(start.getTime() - 3600 * 1000 * 24 * 36500);
      this.currentDate = start;
      this.showStartPicker = true;
    },
    showEndDate() {
      var end = new Date();
      end.setTime(end.getTime() - 3600 * 1000 * 24 * 36500);
      this.currentDate = end;
      this.showEndPicker = true;
    },
    formatter(type, val) {
      if (type === "year") {
        return val + this.$t.meta.year;
      }
      if (type === "month") {
        return val + this.$t.meta.month;
      }
      if (type === "day") {
        return val + this.$t.meta.day;
      }
      return val;
    },
    dateChange(date, type) {
      var formatDate = (val) => {
        var time = new Date(val).toLocaleDateString();
        let [year, month, date] = time.split("/");
        if (month < 10) {
          month = "0" + month;
        }
        if (date < 10) {
          date = "0" + date;
        }
        return year + "-" + month + "-" + date;
      };
      if (type == "start") {
        this.beginTime = formatDate(date);
        this.showStartPicker = false;
      }
      if (type == "end") {
        this.endTime = formatDate(date);
        this.showEndPicker = false;
      }
    },
    getList(page) {
      var param = {
        PageIndex: page,
        PageSize: this.pageSize,
        SerialRegistrationNumber: this.searchType == 1 ? this.searchText : "",
        RemitterName: this.searchType == 2 ? this.searchText : "",
        CustomerNumber: this.searchType == 3 ? this.searchText : "",
        CustomerName: this.searchType == 4 ? this.searchText : "",
        PaymentType: this.paymentType,
        AccountId: this.selectedAccount.value,
        SCId: this.currency,
        MinAmount: this.minAmount,
        MaxAmount: this.maxAmount,
        BeginTime: this.beginTime,
        EndTime: this.endTime,
      };
      this.$api.financialManagement
        .getCustomerCapitalTransferList(param)
        .then((res) => {
          this.tableData = this.tableData.concat(res.data.Data);
          this.loading = false;
          this.error = false;
          this.errorText = "";

          this.page = res.meta.page;
          this.finished = this.page >= res.meta.lastPage;
        })
        .catch((error) => {
          this.loading = false;
          this.error = true;
          this.errorText = error.message;
        });
    },
    pickerShow(pickerName) {
      this[pickerName] = true;
    },
    accountConfirm(e) {
      this.selectedAccount.text = e.text;
      this.selectedAccount.value = e.value;
      this.showAccountPicker = false;
    },
    getCurrencyList() {
      return new Promise((resolve) => {
        this.$api.financialManagement
          .getSystemAvailableCurrencyList()
          .then((res) => {
            this.currencyList = res.data;
            this.initCheckList();
            resolve(true);
          })
          .catch((error) => {
            this.$commonEnv.commonAction(
              this.$commonEnv.actionType.showDialog,
              {
                msg: error.message,
              }
            );
          });
      });
    },
    getCollectionAccountInfoList() {
      return new Promise((resolve) => {
        this.$api.financialManagement
          .getCollectionAccountInfoList({
            CompanyId: this.CompanyId,
            IsEnable: 1,
            PageIndex: 0,
            PageSize: 1000,
          })
          .then((res) => {
            var result = [];
            res.data.Data.forEach((item) => {
              let data = {
                text: item.AccountNickName,
                value: item.AccountId,
              };
              result.push(data);
            });
            this.accountInfoList = result;
            resolve(true);
          })
          .catch((error) => {
            this.$commonEnv.commonAction(
              this.$commonEnv.actionType.showDialog,
              {
                msg: error.message,
              }
            );
          });
      });
    },
  },
};
</script>
<style lang="scss" scoped>
@import "~assets/scss/var";
.accountStatements {
  .searchTop {
    width: 100%;
    background-color: #ffffff;
    border-bottom: 1px solid #ebedf0;
    .search-top-row {
      width: 100%;
      text-align: center;
      background-color: transparent;
      .colItem1 {
        width: 80%;
        text-align: center;
        font-size: 14px;
        .van-search__content {
          padding: 0px;
          margin-right: 10px;
          .van-cell {
            padding: 5px 8px 5px 8px;
          }
        }
        .custom-date {
          padding: 10px;
          .date-btn1 {
            width: 40%;
            display: inline-block;
            border-right: 1px solid #ebedf0;
            line-height: 33px;
            vertical-align: middle;
          }
          .date-btn2 {
            width: 40%;
            display: inline-block;
            line-height: 33px;
            vertical-align: middle;
          }
          .van-button {
            vertical-align: middle;
          }
        }
      }
      .colItem2 {
        width: 20%;
        text-align: center;
        font-size: 14px;
        .isFilter {
          color: $-color-primary;
          font-weight: bold;
        }
      }
    }
  }
  .list {
    .list-container {
      font-size: 12px;
      color: #333;
      .checkBox {
        vertical-align: middle;
        display: inline-block;
        margin: 15px;
      }
      .container {
        padding: 10px;
        box-shadow: 0px 1px 5px 1px rgba(0, 0, 0, 0.2);
        .list-item {
          margin-bottom: 10px;
        }
        .list-item:last-child {
          margin-bottom: 0px;
        }
      }
    }
  }
  .popup-header {
    margin-top: 20px;
    margin-bottom: 10px;
    padding-bottom: 10px;
    text-align: center;
    font-size: 16px;
  }
  .radio-label {
    color: #646566;
    margin-bottom: 10px;
    font-size: 14px;
  }
  .van-radio-group {
    margin-bottom: 20px;
  }
  .van-radio {
    margin-bottom: 10px;
    font-size: 14px;
    ::v-deep .van-radio__label {
      color: #646566;
    }
  }
  .van-form {
    width: 90%;
    margin: auto;
    padding-bottom: 20vw;
    .van-cell:after {
      border: none;
    }
    .van-cell {
      border-bottom: 1px solid #ebedf0;
    }
    .custom-date {
      text-align: center;
      border-bottom: 1px solid #ebedf0;
      padding-bottom: 20px;
      .date-label {
        text-align: left;
        font-size: 14px;
        color: #646566;
        margin-left: 15px;
        margin-bottom: 10px;
        margin-top: 10px;
      }
      .date-btn1 {
        border-radius: 5px;
        color: white;
        border: none;
        vertical-align: middle;
        .van-button__text {
          font-size: 14px !important;
        }
      }
      .date-btn2 {
        margin-left: 30px;
        border-radius: 5px;
        color: white;
        border: none;
        vertical-align: middle;
        .van-button__text {
          font-size: 14px !important;
        }
      }
      .date-txt {
        margin-top: 20px;
        font-size: 14px;
        color: #999;
        border-radius: 10px;
      }
    }
  }
  .popup-footer {
    position: fixed;
    left: 0;
    bottom: 0;
    z-index: 100;
    background-color: white;
    padding-bottom: env(safe-area-inset-bottom);
    width: 100%;
    .reset {
      width: 50%;
      display: inline-block;
      color: $-color-primary;
      background: #ffffff;
      border: none;
    }
    .confirm {
      width: 50%;
      display: inline-block;
      color: #ffffff;
      background: $-color-primary;
      border: none;
    }
  }
  .tool-bar-total {
    font-size: 12px;
    color: #333333;
    background: #fff;
    .tool-bar-total-left {
      display: inline-block;
      width: 25%;
      text-align: center;
      vertical-align: middle;
      .checkbox {
        display: inline-flex;
        span {
          margin-left: 10px;
        }
      }
    }
    .tool-bar-total-item {
      display: inline-block;
      width: 25%;
      text-align: center;
      vertical-align: middle;
      .item1 {
        padding: 5px;
        background: #1e90ff;
        color: #ffffff;
      }
      .item2 {
        padding: 5px;
        background: #e8f4ff;
      }
    }
  }
  .tool-bar {
    box-sizing: border-box;
    position: fixed;
    left: 0;
    bottom: 0;
    width: 100%;
    background-color: white;
    z-index: 100;
    justify-content: space-between;
    padding-bottom: env(safe-area-inset-bottom);
    border-top: 1px solid #f6f6f6;
    .tool-bar-wrapper {
      border-bottom: 1px solid #f6f6f6;
    }
  }
}
</style>